import { useState } from 'react';
import { useT } from '@/store';
import { useMaterials } from '@/hooks/useDirectory';
import {
  useDeleteTonKmRateTier, useSaveTonKmRateTier, useTonKmRateTiers,
} from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { parseDecimalInput } from '@/lib/format';

/**
 * Transporto kainodaros modeliai — tonos-kilometro koeficientų lentelė
 * (betono gamyklų kainodaros modelis): revenue = tonkm_capacity_tons *
 * coefficient * distance_km_one_way. Vienas tenant'as gali turėti kelis
 * atstumo intervalus vienai medžiagai (koeficientas mažėja didėjant
 * atstumui) — todėl tai sąrašas, ne vienas nustatymas, tiksliai tokia pati
 * forma kaip QuarryPriceList.jsx, tik tenant-lygio, ne quarry-lygio.
 *
 * Tuščias material_key (nepasirinkta medžiaga) reiškia "taikoma visoms
 * medžiagoms" — žr. schema.sql'o komentarą tonkm_rate_tiers lentelėje.
 */
export function TonKmRateTiersList() {
  const t = useT();
  const { data: tiers = [], isLoading } = useTonKmRateTiers();
  const { data: materials = [] } = useMaterials();
  const saveTier = useSaveTonKmRateTier();
  const deleteTier = useDeleteTonKmRateTier();
  const [formError, setFormError] = useState(null);

  const [materialKey, setMaterialKey] = useState('');
  const [minDistanceKm, setMinDistanceKm] = useState('');
  const [coefficient, setCoefficient] = useState('');

  const add = () => {
    const distance = parseDecimalInput(minDistanceKm);
    const coef = parseDecimalInput(coefficient);
    if (Number.isNaN(distance)) return setFormError('tonkmRates.errDistance');
    if (Number.isNaN(coef)) return setFormError('tonkmRates.errCoefficient');
    setFormError(null);
    saveTier.mutate(
      { material_key: materialKey || null, min_distance_km: distance, coefficient: coef },
      {
        onSuccess: () => {
          setMaterialKey('');
          setMinDistanceKm('');
          setCoefficient('');
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      <h4 className="panel-title !mb-1">{t('tonkmRates.heading')}</h4>
      <p className="text-[10.5px] leading-snug text-dim">{t('tonkmRates.hint')}</p>

      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : tiers.length === 0 ? (
        <EmptyState>{t('tonkmRates.empty')}</EmptyState>
      ) : (
        <ul className="space-y-1">
          {tiers.map((tier) => (
            <li key={tier.tier_id} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="min-w-0 flex-1 truncate">
                {tier.material_key || t('tonkmRates.allMaterials')}
              </span>
              <input
                className="field !w-20 !py-1 !text-[11px] text-right"
                defaultValue={tier.min_distance_km}
                inputMode="decimal"
                onBlur={(e) => {
                  const value = parseDecimalInput(e.target.value);
                  if (Number.isNaN(value) || value === tier.min_distance_km) return;
                  saveTier.mutate({ tierId: tier.tier_id, min_distance_km: value });
                }}
              />
              <span className="text-dim">{t('tonkmRates.kmUnit')}</span>
              <input
                className="field !w-20 !py-1 !text-[11px] text-right"
                defaultValue={tier.coefficient}
                inputMode="decimal"
                onBlur={(e) => {
                  const value = parseDecimalInput(e.target.value);
                  if (Number.isNaN(value) || value === tier.coefficient) return;
                  saveTier.mutate({ tierId: tier.tier_id, coefficient: value });
                }}
              />
              <ConfirmButton
                label="✕"
                confirmLabel={t('common.confirmDelete')}
                onConfirm={() => deleteTier.mutate(tier.tier_id)}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <select
          className="field !w-auto flex-1 !py-1 !text-[11px]"
          value={materialKey}
          onChange={(e) => setMaterialKey(e.target.value)}
        >
          <option value="">{t('tonkmRates.allMaterials')}</option>
          {materials.map((m) => (
            <option key={m.material_id} value={m.name}>{m.name}</option>
          ))}
        </select>
        <input
          className="field !w-24 !py-1 !text-[11px]"
          value={minDistanceKm}
          onChange={(e) => setMinDistanceKm(e.target.value)}
          placeholder={t('tonkmRates.distancePh')}
          inputMode="decimal"
        />
        <input
          className="field !w-24 !py-1 !text-[11px]"
          value={coefficient}
          onChange={(e) => setCoefficient(e.target.value)}
          placeholder={t('tonkmRates.coefficientPh')}
          inputMode="decimal"
        />
        <button type="button" className="btn" onClick={add} disabled={saveTier.isPending}>
          {t('common.addBtn')}
        </button>
      </div>

      {formError ? <ErrorState error={new Error(t(formError))} /> : null}
      {saveTier.isError ? <ErrorState error={saveTier.error} /> : null}
      {deleteTier.isError ? <ErrorState error={deleteTier.error} /> : null}
    </div>
  );
}
